import { Box, Button, NewModal } from '@platform/ui-library';
import { FC, useEffect, useState } from 'react';
import { FileUploader } from '@app/form_components';
import {
  useCustomNavigate,
  useDesign,
  useServices
} from '@app/hooks';
import { CommonIntent, DesignIntent } from '@app/types';
import {
  createIntentCsvConfig,
  getIntentColumns,
  importFromCsv
} from '@app/utilities';

const intentNameSearch = /^[A-Z_]+$/;

export const ImportFromCsvPage: FC = () => {
  const navigate = useCustomNavigate();
  const serialized = useDesign();
  const {
    errorMessageService,
    intentService,
    loadingService
  } = useServices();
  const [csvText, setCsvText] = useState<string | null>(null);
  const [commonIntents, setCommonIntents] = useState<CommonIntent[]>([]);

  useEffect(() => {
    (async () => {
      ++loadingService.loadingCount;
      setCommonIntents(await intentService.getCommonIntents());
      --loadingService.loadingCount;
    })();
  }, []);

  const onImport = () => {
    if (!csvText) {
      errorMessageService.addError({
        message: 'Please upload a CSV before you continue.'
      });
      return;
    }

    const intents = new Map(
      serialized.components.intents.map((intent) => {
        return [intent.name, intent];
      })
    );

    try {
      importFromCsv<DesignIntent, { Name: string; }>({
        columns: getIntentColumns({
          serialized,
          setterData: {
            commonIntents
          }
        }),
        getItem: ({ Name: name }, index) => {
          name = name.trim();
          if (!name || !intentNameSearch.test(name)) {
            // Add 2 to index to account for header row and 0-based index.
            throw new Error(`Invalid name on row ${index + 2}`);
          }

          let intent = intents.get(name);
          if (!intent) {
            intent = intentService.initializeIntent(false);
            intents.set(name, intent);
          }

          return intent;
        },
        text: csvText
      });
    } catch (error) {
      errorMessageService.addError(error as Error);
    }

    navigate('intent/management');
  };

  return (
    <NewModal isOpen>
      <Box theme="noPadding">
        <div className="box header">
          <h2>Import Intents From CSV</h2>
        </div>
        <div className="box content">
          <FileUploader
            template={createIntentCsvConfig({ serialized })}
            onParse={setCsvText}
          />
        </div>
        <div className="box footer">
          <Button
            onClick={() => {
              navigate('intent/management');
            }}
            theme="outline"
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={onImport}
            theme="default"
            type="button"
          >
            Import
          </Button>
        </div>
      </Box>
    </NewModal>
  );
};
